/**
 * Dynamic GST Bifurcation Utility
 * Determines IGST vs CGST+SGST vs CGST+UTGST based on customer state
 * Astrafin is registered in Karnataka (State Code 29)
 */

export const COMPANY_STATE_CODE = "29";
export const COMPANY_STATE_NAME = "Karnataka";

export type GSTType = 'IGST' | 'CGST_SGST' | 'CGST_UTGST';

export interface GSTSplitResult {
  gstType: GSTType;
  igst: number;
  cgst: number;
  sgst: number;
  utgst: number;
  placeOfSupply: string;
  placeOfSupplyName: string;
}

// Union Territory codes for GST purposes
// Note: Delhi (07) is NOT a UT for GST; J&K (01) became UT but has state GST
const UNION_TERRITORY_CODES = new Set(["04", "25", "26", "31", "34", "35", "38"]);
// "04" Chandigarh, "25" Daman & Diu, "26" Dadra & Nagar Haveli,
// "31" Lakshadweep, "34" Puducherry, "35" Andaman & Nicobar, "38" Ladakh

export const STATE_NAME_MAP: Record<string, string> = {
  "01": "Jammu & Kashmir", "02": "Himachal Pradesh", "03": "Punjab",
  "04": "Chandigarh", "05": "Uttarakhand", "06": "Haryana", "07": "Delhi",
  "08": "Rajasthan", "09": "Uttar Pradesh", "10": "Bihar", "11": "Sikkim",
  "12": "Arunachal Pradesh", "13": "Nagaland", "14": "Manipur",
  "15": "Mizoram", "16": "Tripura", "17": "Meghalaya", "18": "Assam",
  "19": "West Bengal", "20": "Jharkhand", "21": "Odisha",
  "22": "Chhattisgarh", "23": "Madhya Pradesh", "24": "Gujarat",
  "25": "Daman & Diu", "26": "Dadra & Nagar Haveli", "27": "Maharashtra",
  "28": "Andhra Pradesh", "29": "Karnataka", "30": "Goa",
  "31": "Lakshadweep", "32": "Kerala", "33": "Tamil Nadu",
  "34": "Puducherry", "35": "Andaman & Nicobar", "36": "Telangana",
  "37": "Andhra Pradesh (new)", "38": "Ladakh",
};

export const STATE_SHORT_MAP: Record<string, string> = {
  "01": "JK", "02": "HP", "03": "PB", "04": "CG", "05": "UK", "06": "HR",
  "07": "DL", "08": "RJ", "09": "UP", "10": "BR", "11": "SK", "12": "AR",
  "13": "NL", "14": "MN", "15": "MZ", "16": "TR", "17": "ML", "18": "AS",
  "19": "WB", "20": "JH", "21": "OR", "22": "CG", "23": "MP", "24": "GJ",
  "25": "DD", "26": "DN", "27": "MH", "28": "AP", "29": "KA", "30": "GA",
  "31": "LK", "32": "KL", "33": "TN", "34": "PY", "35": "AN", "36": "TS",
  "37": "AP", "38": "LD",
};

/**
 * Extract 2-digit state code from a GSTIN
 */
export function getStateCodeFromGSTIN(gstin: string): string {
  return gstin?.substring(0, 2) || "";
}

/**
 * Determine GST split based on customer GSTIN and company state
 * Total GST amount stays the same — only the split changes
 */
export function determineGSTSplit(
  customerGstin: string,
  gstTotal: number,
  gstRate: number
): GSTSplitResult {
  const customerStateCode = getStateCodeFromGSTIN(customerGstin);
  const placeOfSupply = customerStateCode || COMPANY_STATE_CODE;
  const placeOfSupplyName = STATE_NAME_MAP[placeOfSupply] || "Unknown";

  // If no GSTIN or same state → Intra-state (CGST + SGST)
  if (!customerStateCode || customerStateCode === COMPANY_STATE_CODE) {
    return {
      gstType: 'CGST_SGST',
      igst: 0,
      cgst: Math.round(gstTotal / 2),
      sgst: Math.round(gstTotal / 2),
      utgst: 0,
      placeOfSupply,
      placeOfSupplyName,
    };
  }

  // Union Territory → CGST + UTGST
  if (UNION_TERRITORY_CODES.has(customerStateCode)) {
    return {
      gstType: 'CGST_UTGST',
      igst: 0,
      cgst: Math.round(gstTotal / 2),
      sgst: 0,
      utgst: Math.round(gstTotal / 2),
      placeOfSupply,
      placeOfSupplyName,
    };
  }

  // Inter-state → IGST
  return {
    gstType: 'IGST',
    igst: Math.round(gstTotal),
    cgst: 0,
    sgst: 0,
    utgst: 0,
    placeOfSupply,
    placeOfSupplyName,
  };
}

/**
 * Get GST display labels for an invoice based on its gstType
 */
export function getGSTDisplayLabels(
  gstType: GSTType | undefined,
  gstRate: number,
  gstTotal: number
): Array<{ label: string; amount: number }> {
  const halfRate = gstRate / 2;
  const halfAmount = Math.round(gstTotal / 2);

  switch (gstType) {
    case 'CGST_SGST':
      return [
        { label: `CGST (${halfRate}%)`, amount: halfAmount },
        { label: `SGST (${halfRate}%)`, amount: halfAmount },
      ];
    case 'CGST_UTGST':
      return [
        { label: `CGST (${halfRate}%)`, amount: halfAmount },
        { label: `UTGST (${halfRate}%)`, amount: halfAmount },
      ];
    case 'IGST':
    default:
      return [
        { label: `IGST (${gstRate}%)`, amount: Math.round(gstTotal) },
      ];
  }
}
